import { TIME_SETTINGS } from "../constants/appConstants";
import { formatTimeSpent, parseTimeSpentToSeconds } from "./timeUtils";
import { formatToDDMMYYYY } from "./taskUtils";

const SEC_PER_HOUR = 3600;
const SEC_PER_DAY = TIME_SETTINGS.CORPORATE_WORKING_HOURS * SEC_PER_HOUR;
const MIN_SECONDS = 60;
const MAX_SECONDS = 10 * 365 * SEC_PER_DAY;

const TIME_SPENT_ERROR =
  "Enter a valid duration (e.g. 2d, 1w, 3m, 1y, 2h, 30min, 1h 30min).";

const DECIMAL_HOUR_ERROR =
  "Decimal hours are not allowed. Use compound format (e.g. 1h 30min).";

function buildResult(seconds, apiValue) {
  return {
    valid: true,
    error: null,
    apiValue,
    seconds,
    minutes: Math.round(seconds / 60),
  };
}

function buildInvalid(error = TIME_SPENT_ERROR) {
  return { valid: false, error, apiValue: null, seconds: 0, minutes: 0 };
}

/** Convert seconds to an API duration string (e.g. 2h, 1d, 1h 30min). */
export function secondsToApiTimeSpent(seconds) {
  const formatted = formatTimeSpent(seconds);
  return formatted === "0min" ? null : formatted;
}

/**
 * Parse and validate a time-spent input string.
 * Uses the same corporate-day model as estimated time (8h/day via timeUtils).
 */
export function parseTimeSpentInput(rawInput) {
  const input = String(rawInput ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!input) {
    return buildInvalid("Time spent is required.");
  }

  if (/^\d+\.\d+\s*h(?:\s|$)/i.test(input)) {
    return buildInvalid(DECIMAL_HOUR_ERROR);
  }

  const totalSeconds = parseTimeSpentToSeconds(input);
  if (totalSeconds <= 0) {
    return buildInvalid();
  }

  if (totalSeconds < MIN_SECONDS) {
    return buildInvalid("Duration must be at least 1 minute.");
  }

  if (totalSeconds > MAX_SECONDS) {
    return buildInvalid("Duration is too large.");
  }

  const apiValue = formatTimeSpent(totalSeconds);
  if (apiValue === "0min") {
    return buildInvalid();
  }

  return buildResult(totalSeconds, apiValue);
}

export function secondsToMinutes(seconds) {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value / 60);
}

/**
 * Parse task-level loggedTime / estimatedTime from API.
 * Supports duration strings (e.g. 2d, 1h 30min), numeric seconds, and legacy minute fields.
 */
export function parseTaskTimeField(value) {
  if (value == null || value === "") return 0;

  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return 0;
    return secondsToMinutes(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;

    const fromDuration = parseTimeSpentToSeconds(trimmed);
    if (fromDuration > 0) return secondsToMinutes(fromDuration);

    const numeric = Number(trimmed);
    if (Number.isFinite(numeric) && numeric > 0) {
      return secondsToMinutes(numeric);
    }
  }

  return 0;
}

/** Resolve logged/estimated minutes from task API payload (myTasks, deputedTasks, getTaskById). */
export function resolveTaskTimeMetrics(task) {
  if (!task) {
    return { estimatedMinutes: 0, loggedMinutes: 0 };
  }

  const hasEstimatedTime =
    task.estimatedTime != null || task.estimated_time != null;
  const hasLoggedTime = task.loggedTime != null || task.logged_time != null;

  const estimatedMinutes = hasEstimatedTime
    ? parseTaskTimeField(task.estimated_time ?? task.estimatedTime)
    : Number(task.estimated_minutes ?? task.estimatedMinutes ?? 0) || 0;

  const loggedMinutes = hasLoggedTime
    ? parseTaskTimeField(task.logged_time ?? task.loggedTime)
    : Number(task.logged_minutes ?? task.loggedMinutes ?? 0) || 0;

  return {
    estimatedMinutes: Math.max(0, estimatedMinutes),
    loggedMinutes: Math.max(0, loggedMinutes),
  };
}

/** Attach normalized minute fields for components that read estimated_minutes / logged_minutes.
 *  Also normalizes the new /api/task/list response shape (assigner/assignee nested objects)
 *  into the flat camelCase fields consumed by the table columns and action handlers.
 */
export function enrichTaskWithTimeMetrics(task) {
  if (!task || typeof task !== "object") return task;

  const { estimatedMinutes, loggedMinutes } = resolveTaskTimeMetrics(task);

  // ── Normalize assigner (person who assigned the task) ────────────────────
  // New API shape: task.assigner = { id, first_name, last_name, email, employee_code }
  // Legacy shape:  task.GivenByFirstName / task.GivenByLastName / task.GivenByUserId
  const assigner = task.assigner || {};
  const normalizedAssignedBy = task.assignedBy || {
    userId: assigner.id || task.GivenByUserId || task.assigned_by || null,
    id: assigner.id || task.GivenByUserId || task.assigned_by || null,
    firstName: assigner.first_name || task.GivenByFirstName || "",
    lastName: assigner.last_name || task.GivenByLastName || "",
    email: assigner.email || task.GivenByEmail || "",
    employeeCode: assigner.employee_code || "",
  };

  // ── Normalize assignee (person the task is assigned to) ──────────────────
  // New API shape: task.assignee = { id, first_name, last_name, email, employee_code }
  const assignee = task.assignee || {};
  const normalizedAssignee = {
    userId: assignee.id || task.assigned_to || null,
    id: assignee.id || task.assigned_to || null,
    firstName: assignee.first_name || task.FirstName || "",
    lastName: assignee.last_name || task.LastName || "",
    email: assignee.email || task.email || "",
    employeeCode: assignee.employee_code || "",
  };

  return {
    ...task,
    estimated_minutes: estimatedMinutes,
    logged_minutes: loggedMinutes,
    // Normalized shape consumed by table columns, action handlers, and modals
    assignedBy: normalizedAssignedBy,
    normalizedAssignee,
    // Keep flat legacy fields for backward compat with other components
    GivenByFirstName: normalizedAssignedBy.firstName,
    GivenByLastName: normalizedAssignedBy.lastName,
    GivenByEmail: normalizedAssignedBy.email,
    GivenByUserId: normalizedAssignedBy.id,
    FirstName: normalizedAssignee.firstName,
    LastName: normalizedAssignee.lastName,
  };
}

export function formatLoggedDuration(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return "0min";
  return formatTimeSpent(Math.round(value * 60));
}

export function formatWorklogTimestamp(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";

  const dateLabel = formatToDDMMYYYY(date);
  const timeLabel = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${dateLabel} · ${timeLabel}`;
}

export function getInitialsFromIdentity(name, email) {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const letters = `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.trim();
    if (letters) return letters.toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "NA";
}

export function toDateInputValue(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Convert a YYYY-MM-DD date input to ISO, keeping the selected calendar day
 * in local time and attaching the current clock time (not midnight).
 */
export function dateInputToIso(dateInputValue, now = new Date()) {
  const reference = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();

  if (!dateInputValue) {
    return reference.toISOString();
  }

  const [year, month, day] = dateInputValue.split("-").map(Number);
  if (!year || !month || !day) {
    return reference.toISOString();
  }

  return new Date(
    year,
    month - 1,
    day,
    reference.getHours(),
    reference.getMinutes(),
    reference.getSeconds(),
    reference.getMilliseconds()
  ).toISOString();
}

export function formatMinutesForInput(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return "";

  const wholeHours = Math.floor(value / 60);
  const remainingMinutes = value % 60;

  if (wholeHours > 0 && remainingMinutes > 0) {
    return `${wholeHours}h ${remainingMinutes}min`;
  }
  if (wholeHours > 0) {
    return `${wholeHours}h`;
  }
  return `${remainingMinutes}min`;
}

function resolveWorklogSeconds(timeSpentValue) {
  if (timeSpentValue == null || timeSpentValue === "") return 0;

  if (typeof timeSpentValue === "string") {
    return parseTimeSpentToSeconds(timeSpentValue.trim());
  }

  const numeric = Number(timeSpentValue);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

export function normalizeWorklogFromApi(worklog, fallbackUser = {}) {
  if (!worklog) return null;

  const seconds = resolveWorklogSeconds(worklog.timeSpent ?? worklog.time_spent);
  const minutes = secondsToMinutes(seconds);
  const userName =
    worklog.userName ||
    worklog.user_name ||
    worklog.user?.name ||
    fallbackUser.name ||
    fallbackUser.email ||
    "Unknown user";
  const email = worklog.user?.email || worklog.userEmail || fallbackUser.email;

  return {
    id: worklog.id,
    userName,
    initials: getInitialsFromIdentity(userName, email),
    minutes,
    seconds,
    loggedAt: formatWorklogTimestamp(worklog.date),
    date: worklog.date,
    description: worklog.description || "",
    timeSpentLabel: worklog.timeSpentLabel || formatMinutesForInput(minutes),
  };
}

export function createLocalWorklog(payload, fallbackUser = {}) {
  const parsed = parseTimeSpentInput(payload.timeSpent);
  const userName = fallbackUser.name || fallbackUser.email || "You";

  return {
    id: payload.id || `local-${Date.now()}`,
    userName,
    initials: getInitialsFromIdentity(userName, fallbackUser.email),
    minutes: parsed.minutes,
    seconds: parsed.seconds,
    loggedAt: formatWorklogTimestamp(payload.date),
    date: payload.date,
    description: payload.description || "",
    timeSpentLabel: payload.timeSpent,
  };
}

function startOfLocalDay(date) {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function addLocalDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return startOfLocalDay(next);
}

export function getTodayDateInputValue(now = new Date()) {
  return toDateInputValue(startOfLocalDay(now));
}

/** Allowed log dates: today, yesterday, and previous Friday when today is Monday. */
export function getAllowedWorklogDates(now = new Date(), options = {}) {
  const { extraAllowedDates = [] } = options;
  const today = startOfLocalDay(now);
  const allowedDates = [today, addLocalDays(today, -1)];

  if (today.getDay() === 1) {
    allowedDates.push(addLocalDays(today, -3));
  }

  extraAllowedDates.forEach((dateValue) => {
    if (!dateValue) return;
    const parsed = startOfLocalDay(new Date(`${dateValue}T00:00:00`));
    if (!Number.isNaN(parsed.getTime())) {
      allowedDates.push(parsed);
    }
  });

  const seen = new Set();
  return allowedDates
    .map((date) => toDateInputValue(date))
    .filter((dateValue) => {
      if (!dateValue || seen.has(dateValue)) return false;
      seen.add(dateValue);
      return true;
    })
    .sort();
}

export function isAllowedWorklogDate(dateInputValue, now = new Date(), options = {}) {
  if (!dateInputValue) return false;
  return getAllowedWorklogDates(now, options).includes(dateInputValue);
}

/** Whether a work-log entry can be edited based on its logged date. */
export function canEditWorklogEntry(worklog, now = new Date()) {
  if (!worklog) return false;
  return isAllowedWorklogDate(toDateInputValue(worklog.date), now);
}

export function getWorklogDateInputBounds(now = new Date(), options = {}) {
  const allowedDates = getAllowedWorklogDates(now, options);
  if (allowedDates.length === 0) {
    return { min: "", max: "" };
  }

  return {
    min: allowedDates[0],
    max: allowedDates[allowedDates.length - 1],
  };
}

export function getWorklogDateRestrictionMessage(now = new Date()) {
  return startOfLocalDay(now).getDay() === 1
    ? "You can only log time for today, yesterday, or last Friday."
    : "You can only log time for today or yesterday.";
}

/**
 * Validates and resolves a work-log date picker change.
 * Returns the next date value plus an optional error message.
 */
export function handleWorklogDateInputChange(
  currentDate,
  nextDate,
  now = new Date(),
  options = {}
) {
  const allowedDates = getAllowedWorklogDates(now, options);
  const fallbackDate = allowedDates[allowedDates.length - 1] || getTodayDateInputValue(now);

  if (!nextDate) {
    return { date: fallbackDate, dateError: "" };
  }

  if (isAllowedWorklogDate(nextDate, now, options)) {
    return { date: nextDate, dateError: "" };
  }

  return {
    date: currentDate || fallbackDate,
    dateError: getWorklogDateRestrictionMessage(now),
  };
}

export { TIME_SPENT_ERROR, DECIMAL_HOUR_ERROR };
